import { useEffect, useRef, useState } from 'react'
import { NavLink, useLocation, useNavigate } from 'react-router-dom'
import {
  Home, Users2, Truck, GraduationCap,
  UtensilsCrossed, Hotel, Factory, Stethoscope, Building2,
  Briefcase, ShoppingBag as ShoppingBagIcon,
  X, LogOut, Settings2, ChevronRight, ChevronLeft, ChevronDown,
  UserCircle, ShoppingBag, Wallet, Cog, Boxes, HelpCircle,
} from 'lucide-react'
import { COMING_SOON_VERTICALS, SHOW_COMING_SOON_VERTICALS } from '../../lib/verticals'
import { cn } from '../../utils/cn'
import { useAuthStore } from '../../store/authStore'
import { useUIStore } from '../../store/uiStore'
import { supabase } from '../../lib/supabase'
import { logAuthEvent } from '../../lib/auditLog'
import { useQuery } from '@tanstack/react-query'
import { usePermissions } from '../../hooks/usePermissions'
import { useTenantAccess } from '../../hooks/useTenantAccess'
import { useEnforcement } from '../../hooks/useEnforcement'
import { useTradingAttention } from '../../lib/attention/tradingAttention'
import { useCurrentUserPagePermissions } from '../../lib/permissions/usePagePermissions'
import { PAGE_REGISTRY } from '../../lib/permissions/pageRegistry'
import { NEW_NAV } from '../../lib/nav/navFlag'
import { AccordionNav } from './AccordionNav'
import toast from 'react-hot-toast'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = (t: string): any => (supabase as any).from(t)

interface NavItemProps {
  to: string
  icon: React.ReactNode
  label: string
  badge?: number
  badgeClassName?: string
  matchPaths?: string[]
  collapsed?: boolean
  onClose: () => void
}

function NavItem({ to, icon, label, badge, badgeClassName, matchPaths, collapsed, onClose }: NavItemProps) {
  const { pathname } = useLocation()
  const externallyActive = matchPaths?.some(p => pathname === p || pathname.startsWith(p + '/')) ?? false
  return (
    <NavLink
      to={to}
      onClick={onClose}
      title={collapsed ? label : undefined}
      className={({ isActive }) =>
        cn(
          'relative flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors w-full',
          collapsed && 'lg:justify-center lg:px-2',
          (isActive || externallyActive)
            ? 'border-l-2 border-brand-500 text-brand-600 bg-brand-50'
            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900',
        )
      }
    >
      <span className="text-[18px] leading-none flex-shrink-0">{icon}</span>
      <span className={cn('flex-1 truncate', collapsed && 'lg:hidden')}>{label}</span>
      {badge != null && badge > 0 && (
        <>
          <span className={cn(
            'ml-auto text-white text-xs rounded-full px-1.5 py-0.5 min-w-[18px] text-center',
            badgeClassName ?? 'bg-brand-500',
            collapsed && 'lg:hidden',
          )}>
            {badge}
          </span>
          {collapsed && (
            <span className={cn(
              'hidden lg:block absolute top-1 right-1 h-2 w-2 rounded-full ring-2 ring-white',
              badgeClassName ?? 'bg-brand-500',
            )} />
          )}
        </>
      )}
    </NavLink>
  )
}

function ComingSoonNavItem({ icon, label }: { icon: React.ReactNode; label: string }) {
  return (
    <div
      title="Available in a future release"
      className="flex items-center gap-3 pl-10 pr-3 py-2 rounded-xl text-sm font-medium text-slate-500 cursor-not-allowed opacity-60 select-none"
    >
      <span className="text-[16px] leading-none flex-shrink-0">{icon}</span>
      <span className="flex-1 truncate">{label}</span>
      <span className="ml-auto text-xs bg-slate-100 text-slate-500 rounded-full px-2 py-0.5">Soon</span>
    </div>
  )
}

interface OtherModulesSectionProps {
  modules: Array<{ code: string; label: string; icon: React.ReactNode }>
  collapsed?: boolean
}

function OtherModulesSection({ modules, collapsed }: OtherModulesSectionProps) {
  const STORAGE_KEY = 'sidebar.otherModules.expanded'
  const [expanded, setExpanded] = useState<boolean>(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY)
      if (raw === null) return false
      return raw === 'true'
    } catch {
      return false
    }
  })

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, String(expanded)) } catch { /* ignore */ }
  }, [expanded])

  const sorted = [...modules].sort((a, b) => a.label.localeCompare(b.label))

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setExpanded(e => !e)}
        aria-expanded={expanded}
        title={collapsed ? 'Other Modules' : undefined}
        className={cn(
          'flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors w-full text-left text-gray-600 hover:bg-gray-50 hover:text-gray-900',
          collapsed && 'lg:justify-center lg:px-2',
        )}
      >
        <span className="text-[18px] leading-none flex-shrink-0">
          <Boxes className="h-[18px] w-[18px]" />
        </span>
        <span className={cn('flex-1 truncate', collapsed && 'lg:hidden')}>Other Modules</span>
        <ChevronRight
          className={cn(
            'h-4 w-4 text-gray-400 flex-shrink-0 transition-transform',
            expanded && 'rotate-90',
            collapsed && 'lg:hidden',
          )}
        />
      </button>

      {expanded && (
        <div className={cn(
          'mt-0.5 space-y-0.5',
          // When the rail is collapsed (lg+), pop the list out to the right as a
          // flyout instead of pushing the rail open.
          collapsed && 'lg:absolute lg:left-full lg:top-0 lg:ml-1 lg:mt-0 lg:z-50 lg:w-56 lg:rounded-xl lg:border lg:border-gray-100 lg:bg-white lg:shadow-lg lg:p-1',
        )}>
          {sorted.map(mod => (
            <ComingSoonNavItem key={mod.code} icon={mod.icon} label={mod.label} />
          ))}
        </div>
      )}
    </div>
  )
}

const VERTICAL_ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  ShoppingBag: ShoppingBagIcon,
  UtensilsCrossed,
  GraduationCap,
  Hotel,
  Truck,
  Stethoscope,
  Building2,
  Factory,
  Users2,
  Briefcase,
}

const SOON_VERTICALS: Array<{ code: string; label: string; icon: React.ReactNode }> = COMING_SOON_VERTICALS.map(v => {
  const Icon = VERTICAL_ICON_MAP[v.icon] ?? Boxes
  return { code: v.code, label: v.label, icon: <Icon className="h-4 w-4" /> }
})

export function Sidebar() {
  const { user, tenant, clearSession } = useAuthStore()
  const { sidebarOpen, setSidebarOpen } = useUIStore()
  const navigate = useNavigate()
  const { isOwner, isAdmin } = usePermissions()
  const { hasPage, loadingPages } = useTenantAccess()
  const { enforced } = useEnforcement()
  const pagePerms = useCurrentUserPagePermissions()

  // Phase 3 page-level gate. Visibility precedence mirrors <PermissionGuard>:
  //   1. super_admin → always visible (platform role bypasses everything)
  //   2. plan gate → applies to owner AND staff once enforcement is on; held
  //      while plan pages load so we don't flash an empty sidebar
  //   3. owner, or a role with no page permissions seeded yet → role gate
  //      bypassed (the plan gate above still applied)
  //   4. staff → the role's level for the page must not be 'none'
  const hasRolePerms = pagePerms.levels.size > 0
  function pageVisible(pageKey: string): boolean {
    if (pagePerms.isSuperAdmin) return true
    if (enforced && !loadingPages && !hasPage(pageKey)) return false
    if (pagePerms.isOwner || !hasRolePerms) return true
    if (pagePerms.getLevel(pageKey) === 'none') return false
    return true
  }
  // True if user can view at least one page in the given pageRegistry module.
  function moduleVisible(moduleName: string): boolean {
    if (pagePerms.isSuperAdmin) return true
    return PAGE_REGISTRY
      .filter(p => p.module === moduleName)
      .some(p => pageVisible(p.key))
  }

  const tid = tenant?.id ?? ''
  const uid = user?.id ?? ''
  const [logoutBlockSessions, setLogoutBlockSessions] = useState<{ id: string; locationName: string }[]>([])

  // Icon-rail collapse (desktop only; mobile always renders the full drawer).
  // Persisted so the choice survives reloads.
  const COLLAPSE_KEY = 'sidebar.collapsed'
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try { return localStorage.getItem(COLLAPSE_KEY) === 'true' } catch { return false }
  })
  useEffect(() => {
    try { localStorage.setItem(COLLAPSE_KEY, String(collapsed)) } catch { /* ignore */ }
  }, [collapsed])

  const [tenantMenuOpen, setTenantMenuOpen] = useState(false)
  const tenantMenuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!tenantMenuOpen) return
    function handleClickOutside(event: MouseEvent) {
      if (tenantMenuRef.current && !tenantMenuRef.current.contains(event.target as Node)) {
        setTenantMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [tenantMenuOpen])

  // Single source of truth for the Trading "needs attention" count. The same
  // model cascades to the Trading sub-nav, the Expenses tabs and the vendor-group
  // chips, so every level reconciles (Trading = Σ children) and resolving one
  // item updates them all. Online-orders leaf gated to users who can see it.
  const tradingAttention = useTradingAttention(tid, pageVisible('sell.online_orders'))

  const { data: palstroUnread = 0 } = useQuery<number>({
    queryKey: ['palstro-unread', tid],
    enabled: !!tid,
    refetchInterval: 60 * 1000,
    queryFn: async () => {
      const { count } = await db('palstro_messages')
        .select('*', { count: 'exact', head: true })
        .eq('tenant_id', tid)
        .eq('is_read', false)
      return count ?? 0
    },
  })

  async function handleLogout() {
    setTenantMenuOpen(false)
    const { data: openSessions } = await db('pos_sessions')
      .select('id, warehouses(name)')
      .eq('tenant_id', tid)
      .eq('user_id', uid)
      .eq('status', 'open')
      .is('closed_at', null)
    const open = (openSessions ?? []) as { id: string; warehouses: { name: string } | null }[]
    if (open.length > 0) {
      setLogoutBlockSessions(open.map(s => ({ id: s.id, locationName: s.warehouses?.name ?? 'Unknown' })))
      return
    }
    // Audit the sign-out BEFORE signOut() — after it the session is gone and RLS
    // would reject the insert. Best-effort (swallows its own errors).
    await logAuthEvent(tid, uid, 'logout', 'Signed out')
    await supabase.auth.signOut()
    clearSession()
    toast.success('Logged out. See you soon!')
    // Manual sign-out lands on the public marketing homepage (RootRedirect renders it
    // for logged-out users). Idle auto-logout (IdleLogout) still goes to /login so the
    // person can re-authenticate.
    navigate('/')
  }

  const close = () => setSidebarOpen(false)
  const plan = tenant?.plan ?? 'starter'

  const canManageBusinessSettings = isOwner || isAdmin

  return (
    <>
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/40 backdrop-blur-sm lg:hidden"
          onClick={close}
        />
      )}

      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-40 flex w-64 flex-col bg-white shadow-xl transition-all duration-300 ease-in-out lg:relative lg:translate-x-0 lg:shadow-none lg:border-r lg:border-gray-100',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full',
          collapsed ? 'lg:w-16' : 'lg:w-64',
        )}
      >
        {/* Collapse handle — pinned to the right edge (desktop only). Mobile uses
            the slide-in drawer, so it stays hidden there. */}
        <button
          type="button"
          onClick={() => setCollapsed(c => !c)}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          className="hidden lg:flex absolute top-5 -right-3 z-50 h-6 w-6 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-400 shadow-md transition-colors hover:border-brand-300 hover:text-brand-600"
        >
          {collapsed
            ? <ChevronRight className="h-3.5 w-3.5" />
            : <ChevronLeft className="h-3.5 w-3.5" />}
        </button>

        {/* Header — tenant tile with dropdown */}
        <div className="relative flex items-center justify-between px-4 py-4 border-b border-gray-100" ref={tenantMenuRef}>
          <button
            type="button"
            onClick={() => setTenantMenuOpen(o => !o)}
            className={cn(
              'flex items-center gap-2.5 min-w-0 flex-1 rounded-xl px-1.5 py-1 -mx-1.5 hover:bg-slate-50 transition-colors text-left',
              collapsed && 'lg:justify-center',
            )}
            title={collapsed ? (tenant?.name ?? 'Palstro') : undefined}
            aria-haspopup="menu"
            aria-expanded={tenantMenuOpen}
          >
            <div className="h-8 w-8 rounded-xl bg-brand-500 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
              {tenant?.name?.charAt(0) ?? 'P'}
            </div>
            <div className={cn('min-w-0 flex-1', collapsed && 'lg:hidden')}>
              <p className="text-sm font-bold text-gray-900 truncate">{tenant?.name ?? 'Palstro'}</p>
              <p className="text-xs text-gray-500 truncate">{tenant?.currency ?? 'NGN'} · {plan}</p>
            </div>
            <ChevronDown className={cn('h-4 w-4 text-gray-400 flex-shrink-0 transition-transform', tenantMenuOpen && 'rotate-180', collapsed && 'lg:hidden')} />
          </button>
          <button
            onClick={close}
            className="lg:hidden ml-1 rounded-lg p-1 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>

          {tenantMenuOpen && (
            <div
              role="menu"
              className="absolute left-3 top-full mt-1 z-50 min-w-[220px] rounded-xl border border-gray-100 bg-white shadow-lg py-1.5"
            >
              <button
                role="menuitem"
                type="button"
                onClick={() => { setTenantMenuOpen(false); close(); navigate('/settings/profile') }}
                className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                <UserCircle className="h-4 w-4 text-gray-500" />
                My Profile
              </button>
              <div className="my-1 border-t border-gray-100" />
              <button
                role="menuitem"
                type="button"
                onClick={handleLogout}
                className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-red-600 hover:bg-red-50"
              >
                <LogOut className="h-4 w-4" />
                Logout
              </button>
            </div>
          )}
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto px-3 py-3 space-y-0.5">
          {NEW_NAV ? (
            <AccordionNav
              collapsed={collapsed}
              onClose={close}
              canSee={pageVisible}
              canManageBusinessSettings={canManageBusinessSettings}
              attention={tradingAttention}
            />
          ) : (
          <>
          {canManageBusinessSettings && moduleVisible('Business Settings') && (
            <NavItem
              to="/business-settings"
              icon={<Settings2 className="h-[18px] w-[18px]" />}
              label="Business Settings"
              collapsed={collapsed}
              onClose={close}
            />
          )}

          {/* Dashboard — always visible (but respects page-level grants) */}
          {pageVisible('dashboard') && (
            <NavItem to="/dashboard" icon={<Home className="h-[18px] w-[18px]" />} label="Dashboard" collapsed={collapsed} onClose={close} />
          )}

          {moduleVisible('Payroll') && (
            <NavItem to="/payroll" icon={<Users2 className="h-[18px] w-[18px]" />} label="Payroll" collapsed={collapsed} onClose={close} />
          )}

          {/* Trading — clickable parent; sub-nav lives inside TradingLayout */}
          {moduleVisible('Trading') && (
            <NavItem
              to="/trading"
              icon={<ShoppingBag className="h-[18px] w-[18px]" />}
              label="Trading"
              badge={tradingAttention.total}
              badgeClassName="bg-red-500"
              matchPaths={['/buy', '/sell', '/inventory', '/contacts', '/expenses']}
              collapsed={collapsed}
              onClose={close}
            />
          )}

          {/* Finance — clickable parent; sub-nav lives inside FinanceLayout */}
          {moduleVisible('Finance') && (
            <NavItem
              to="/finance"
              icon={<Wallet className="h-[18px] w-[18px]" />}
              label="Finance"
              collapsed={collapsed}
              onClose={close}
            />
          )}

          {/* System — clickable parent; sub-nav lives inside SystemLayout */}
          {moduleVisible('System') && (
            <NavItem
              to="/system"
              icon={<Cog className="h-[18px] w-[18px]" />}
              label="System"
              badge={palstroUnread}
              collapsed={collapsed}
              onClose={close}
            />
          )}

          {/* Other Modules — collapsible, default collapsed.
              Hidden while non-Retail verticals ship as separate products
              (SHOW_COMING_SOON_VERTICALS in lib/verticals.ts). */}
          {SHOW_COMING_SOON_VERTICALS && (
            <OtherModulesSection modules={SOON_VERTICALS} collapsed={collapsed} />
          )}

          {/* Help — always available to every role. */}
          <NavItem
            to="/help"
            icon={<HelpCircle className="h-[18px] w-[18px]" />}
            label="Help"
            collapsed={collapsed}
            onClose={close}
          />
          </>
          )}
        </nav>

        {/* User footer */}
        <div className="border-t border-gray-100 p-3">
          <div className={cn('flex items-center gap-3 rounded-xl px-3 py-2.5', collapsed && 'lg:justify-center lg:px-0')}>
            <div
              className="h-8 w-8 rounded-full bg-brand-100 flex items-center justify-center text-brand-700 font-semibold text-sm flex-shrink-0"
              title={collapsed ? (user?.full_name ?? user?.email ?? 'User') : undefined}
            >
              {user?.full_name?.charAt(0) ?? user?.email?.charAt(0) ?? 'U'}
            </div>
            <div className={cn('flex-1 min-w-0', collapsed && 'lg:hidden')}>
              <p className="text-sm font-medium text-gray-900 truncate">{user?.full_name ?? 'User'}</p>
              <p className="text-xs text-gray-500 truncate">{user?.email}</p>
            </div>
            <button
              onClick={handleLogout}
              className={cn('rounded-lg p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors', collapsed && 'lg:hidden')}
              title="Sign out"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </aside>

      {/* ─── Logout block modal ──────────────────────────────────────────── */}
      {logoutBlockSessions.length > 0 && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 px-4">
          <div className="card w-full max-w-sm space-y-4">
            <div>
              <h3 className="font-bold text-gray-900">Close POS Sessions First</h3>
              <p className="text-sm text-gray-500 mt-1">
                You have open POS sessions. Close them before logging out.
              </p>
            </div>
            <div className="space-y-2">
              {logoutBlockSessions.map(s => (
                <a
                  key={s.id}
                  href={`/pos/session/${s.id}`}
                  className="flex items-center justify-between px-3 py-2.5 rounded-xl border border-gray-200 hover:border-orange-400 hover:bg-orange-50 transition-colors"
                  onClick={() => setLogoutBlockSessions([])}
                >
                  <span className="text-sm font-medium text-gray-800">{s.locationName}</span>
                  <span className="text-xs text-orange-500 font-semibold">Go to session →</span>
                </a>
              ))}
            </div>
            <button
              className="btn-secondary w-full"
              onClick={() => setLogoutBlockSessions([])}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </>
  )
}
